import { useEffect, useState } from "react";
import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";
import {
  DEFAULT_METRICS_DATE_RANGE,
  MetricsDateRange,
} from "./metricsDateRange";
import { HospitalCompetitionRank } from "./hospitalCompetitionRanking";

export type DomainCompetition = {
  domainId: string;
  domainName: string;
  ranks: HospitalCompetitionRank[];
};

export type UseAllDomainsFeedbackCompetitionResult = {
  domains: DomainCompetition[];
  loading: boolean;
  error: boolean;
  /** Why there is nothing to rank, for callers that explain it to admins. */
  emptyReason:
    | "no-domains"
    | "single-hospital-domains"
    | "no-learner-data"
    | "no-org"
    | "no-domain"
    | null;
};

type ApiResponse = {
  domains?: DomainCompetition[];
  emptyReason?: string | null;
};

/**
 * Loads ordinal hospital rankings from the API.
 * Peer-hospital learner rows stay on the server (tenant RLS + FERPA); only
 * ranks are returned to the client.
 */
export function useAllDomainsFeedbackCompetition(
  dateRange: MetricsDateRange = DEFAULT_METRICS_DATE_RANGE,
  _accessibleOrgIds?: string[],
): UseAllDomainsFeedbackCompetitionResult {
  const [domains, setDomains] = useState<DomainCompetition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [emptyReason, setEmptyReason] =
    useState<UseAllDomainsFeedbackCompetitionResult["emptyReason"]>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    API.get<ApiResponse>(API_ROUTES.HOSPITAL_COMPETITION.LIST(dateRange))
      .then((response) => {
        if (cancelled) return;
        const nextDomains = response.data.domains || [];
        setDomains(nextDomains);
        const reason = response.data.emptyReason;
        if (nextDomains.length === 0) {
          if (
            reason === "no-domains" ||
            reason === "single-hospital-domains" ||
            reason === "no-learner-data" ||
            reason === "no-org" ||
            reason === "no-domain"
          ) {
            setEmptyReason(reason);
          } else {
            setEmptyReason("no-domains");
          }
        } else {
          setEmptyReason(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDomains([]);
        setError(true);
        setEmptyReason(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateRange]);

  return { domains, loading, error, emptyReason };
}
